import moment from 'moment-mini'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs'
import { distinctUntilChanged, map, take, tap } from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { Booking } from 'src/app/features/booking/booking'
import { bookingActions } from 'src/app/features/booking/booking.actions'
import { bookingSelectors } from 'src/app/features/booking/booking.selectors'
import { participationSelectors } from 'src/app/features/booking/participation/participation.selectors'
import { Product } from 'src/app/features/product/product'
import { productGroupSelectors } from 'src/app/features/product/product-group/product-group.selectors'
import { productSelectors } from 'src/app/features/product/product.selectors'

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit
} from '@angular/core'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Actions } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import { saleItemSelectors } from '../../sale-item/sale-item.selectors'
import { OrganizeBookingService } from '../organize-booking.service'
import { OrganizeBookingsModalComponent } from '../organize-bookings-modal/organize-bookings-modal.component'
import {
  CellState,
  ParticipationByProductDate
} from '../participationbyproductdate'

@UntilDestroy()
@Component({
  selector: 'organize-bookings-interface',
  templateUrl: './organize-bookings-interface.component.html',
  styleUrls: ['./organize-bookings-interface.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrganizeBookingsInterfaceComponent implements OnInit {
  @Input() selectedProductGroupId$: Observable<number>
  @Input() startDate: moment.Moment
  @Input() endDate: moment.Moment
  @Input() saleBookingIds: number[]
  @Input() saleId: number

  public bookings$: Observable<Booking[]>
  public bookings: Booking[]
  public productsInSelectedGroup$: Observable<Product[]>
  public selectedLanguage$: Observable<string>
  public datesToShow: Date[]
  public isLoading$: Observable<boolean>

  public bsModalRef: BsModalRef

  public participantCounts: { [key: string]: ParticipationByProductDate } = {}
  public initialParticipantCounts: {
    [key: string]: number
  } = {}

  public activeSortingField: string = 'rooms_asc'
  public activeSortingField$: Subject<string> = new BehaviorSubject<string>(
    'rooms_asc'
  )

  constructor(
    private store: Store<AppState>,
    private modalService: BsModalService,
    private organizeBookingService: OrganizeBookingService,
    private actions$: Actions,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )

    this.isLoading$ = this.store.select(bookingSelectors.selectIsLoading)

    this.datesToShow = []
    for (
      let m = moment(this.startDate).startOf('day');
      m.diff(moment(this.endDate).startOf('day'), 'days') <= 0;
      m.add(1, 'days')
    ) {
      this.datesToShow.push(m.toDate())
    }

    this.selectedProductGroupId$
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe((selectedProductGroupId) => {
        this.participantCounts = {}

        this.productsInSelectedGroup$ = combineLatest([
          this.store.select(productSelectors.selectAll),
          this.store.select(
            productGroupSelectors.selectById,
            selectedProductGroupId
          ),
          this.activeSortingField$
        ]).pipe(
          map(([products, productGroup, sortingField]) =>
            products
              .filter((p) => productGroup.products?.includes(p.id))
              .sort((a, b) => {
                switch (sortingField) {
                  case 'rooms_asc':
                    return a.description?.localeCompare(b.description)
                  case 'rooms_desc':
                    return b.description?.localeCompare(a.description)
                  case 'size_asc':
                    return a.maxParticipants - b.maxParticipants
                  case 'size_desc':
                    return b.maxParticipants - a.maxParticipants
                  default:
                    return a.description?.localeCompare(b.description)
                }
              })
          )
        )

        this.productsInSelectedGroup$
          .pipe(
            distinctUntilChanged(
              (a, b) => JSON.stringify(a) == JSON.stringify(b)
            ),
            untilDestroyed(this)
          )
          .subscribe((products) => {
            if (!this.startDate || !this.endDate) {
              return
            }

            this.store.dispatch(
              bookingActions.fetchMany({
                params: RequestQueryBuilder.create({
                  search: {
                    productId: {
                      [CondOperator.IN]: products.map((p) => p.id)
                    },
                    startDate: {
                      [CondOperator.BETWEEN]: [
                        this.startDate.toISOString(),
                        this.endDate.toISOString()
                      ]
                    }
                  },
                  join: [
                    { field: 'product' },
                    { field: 'linkedProduct' },
                    { field: 'saleItems' },
                    { field: 'participations' }
                  ]
                })
              })
            )
          })

        this.bookings$ = combineLatest([
          this.store.select(bookingSelectors.selectAll),
          this.store.select(productSelectors.selectEntities),
          this.productsInSelectedGroup$
        ]).pipe(
          map(([bookings, productEntities, selectedProducts]) => {
            const filteredBookings = bookings.filter((booking) => {
              if (this.startDate.isAfter(moment(booking.startDate))) {
                return false
              }

              if (this.endDate.isBefore(moment(booking.startDate))) {
                return false
              }

              const product = productEntities[booking.productId]

              if (!product) {
                return false
              }

              return selectedProducts.map((p) => p.id).includes(product.id)
            })
            return filteredBookings
          }),
          tap((bookings) => {
            this.bookings = bookings
          })
        )

        this.setUpParticipantCounts()
      })
  }

  public onHeaderClick(field: string) {
    this.activeSortingField =
      this.activeSortingField == `${field}_asc`
        ? `${field}_desc`
        : `${field}_asc`

    this.activeSortingField$.next(this.activeSortingField)
  }

  public isCellBooked(product: Product, date: Date): boolean {
    if (!date) {
      return false
    }

    const key = `${product.id}_${moment(date).format('YYYY-MM-DD')}`
    const isBooked = this.participantCounts[key]?.currentParticipantCount > 0
    if (isBooked) {
      if (this.participantCounts[key]) {
        this.participantCounts[key].state = CellState.BOOKED
      }

      return true
    }
    return false
  }

  public isCellUnavailable(product: Product, date: Date): boolean {
    if (!date) {
      return true
    }
    const dateString = moment(date).format('YYYY-MM-DD')
    const key = `${product.id}_${dateString}`

    const isUnavailable = !Boolean(
      Object.keys(this.participantCounts).find(
        (key) =>
          key.includes(dateString) && this.participantCounts[key]?.saleItemId
      )
    )

    if (isUnavailable) {
      if (this.participantCounts[key]) {
        this.participantCounts[key].state = CellState.UNAVAILABLE
      }
      return true
    }

    return false
  }

  public isCellOccupied(product: Product, date: Date): boolean {
    if (!date) {
      return false
    }

    const key = `${product.id}_${moment(date).format('YYYY-MM-DD')}`

    const filteredBookings = this.getFilteredBookingsByDateAndProduct(
      this.bookings,
      product,
      date
    )

    if (filteredBookings?.length == 0) {
      return false
    }

    const isOccupied = Boolean(
      filteredBookings?.find(
        (booking) =>
          !this.saleBookingIds.includes(booking.id) &&
          booking?.participations?.length > 0
      )
    )

    if (isOccupied) {
      if (this.participantCounts[key]) {
        this.participantCounts[key].state = CellState.OCCUPIED
      }

      return true
    }
    return false
  }

  private getFilteredBookingsByDateAndProduct(
    bookings: Booking[],
    product: Product,
    date: Date
  ): Booking[] {
    return bookings?.filter((booking) => {
      if (moment(date).startOf('day').isAfter(moment(booking.startDate))) {
        return false
      }

      if (moment(date).endOf('day').isBefore(moment(booking.startDate))) {
        return false
      }

      return (
        product.id == booking.productId || product.id == booking.linkedProductId
      )
    })
  }

  public onCellClick(product: Product, date: Date) {
    const formattedDate: string = moment(date).format('YYYY-MM-DD')
    const key = `${product.id}_${formattedDate}`

    this.bsModalRef?.hide()

    combineLatest([
      this.bookings$,
      this.store.select(productSelectors.selectEntities),
      this.store.select(participationSelectors.selectAll),
      this.store.select(saleItemSelectors.selectEntities),
      this.productsInSelectedGroup$
    ])
      .pipe(take(1))
      .subscribe(
        ([
          bookings,
          productEntities,
          participations,
          saleItemEntities,
          productsInSelectedGroup
        ]) => {
          let targetSaleItemId = this.participantCounts[key]?.saleItemId

          if (!targetSaleItemId) {
            const participantCountKeysOnDate = Object.keys(
              this.participantCounts
            ).filter((key) => key.includes(formattedDate))

            const targetKey = participantCountKeysOnDate.find(
              (key) => this.participantCounts[key]?.saleItemId
            )

            if (!targetKey) {
              console.error('No saleitem found, terminating process')
              return
            }

            targetSaleItemId = this.participantCounts[targetKey].saleItemId
          }

          const targetSaleItem = saleItemEntities[targetSaleItemId] ?? null

          const targetLinkedProductId =
            targetSaleItem?.params?.bookableLinkedProductId ??
            targetSaleItem.productId

          this.bsModalRef = this.modalService.show(
            OrganizeBookingsModalComponent,
            {
              initialState: {
                key,
                participantCounts: this.participantCounts,
                maxNumOfParticipants: product.maxParticipants,
                targetLinkedProductId,
                bookings,
                participations,
                productsInSelectedGroup
              },
              class: 'modal-dialog-scrollable'
            }
          )
        }
      )
  }

  public getNumberOfMyCurrentParticipationsByDate(date: Date): number {
    const keys = Object.keys(this.participantCounts).filter((key) =>
      key.includes(moment(date).format('YYYY-MM-DD'))
    )
    let numOfParticipants = 0
    for (const key of keys) {
      numOfParticipants += this.participantCounts[key]?.currentParticipantCount
    }
    return numOfParticipants
  }

  public getNumberOfMyOriginalParticipationsByDate$(
    date: Date
  ): Observable<number> {
    return this.productsInSelectedGroup$.pipe(
      take(1),
      map((products) => {
        const keys = Object.keys(this.initialParticipantCounts)
          .filter((key) => key.includes(moment(date).format('YYYY-MM-DD')))
          .filter((key) => {
            const productId = key.split('_')[0]
            return products.map((p) => p?.id).includes(Number(productId))
          })

        let numOfParticipants = 0
        for (const key of keys) {
          numOfParticipants += this.initialParticipantCounts[key]
        }

        return numOfParticipants
      })
    )
  }

  public getNumberOfParticipantsForResource(
    product: Product,
    date: Date
  ): number {
    const key = `${product.id}_${moment(date).format('YYYY-MM-DD')}`
    return this.participantCounts[key]?.currentParticipantCount
  }

  private setUpParticipantCounts() {
    combineLatest([
      this.productsInSelectedGroup$,
      this.bookings$,
      this.store.select(participationSelectors.selectAll),
      this.store.select(saleItemSelectors.selectEntities)
    ])
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged((a, b) => JSON.stringify(a) == JSON.stringify(b))
      )
      .subscribe(([products, bookings, participations, saleItemEntities]) => {
        this.participantCounts = {}
        for (let product of products) {
          for (let date of this.datesToShow) {
            const key = `${product.id}_${moment(date).format('YYYY-MM-DD')}`

            const filteredBookings = this.getFilteredBookingsByDateAndProduct(
              bookings,
              product,
              date
            )

            const filteredParticipations = participations
              .filter((p) =>
                filteredBookings
                  .map((booking) => booking.id)
                  .includes(p.bookingId)
              )
              .filter((p) => {
                if (!p?.saleItemId) {
                  return false
                }

                const saleItem = saleItemEntities[p.saleItemId]

                if (!saleItem || saleItem.saleId != this.saleId) {
                  return false
                }

                return true
              })

            const numOfParticipants = filteredParticipations.length

            if (this.initialParticipantCounts[key] == null) {
              this.initialParticipantCounts[key] = numOfParticipants
            }

            this.participantCounts[key] = {
              currentParticipantCount: numOfParticipants,
              originalParticipantCount: numOfParticipants,
              saleItemId:
                numOfParticipants > 0
                  ? filteredParticipations[0]?.saleItemId
                  : null,
              targetLinkedProductId: null,
              state: CellState.AVAILABLE
            }
          }
        }
      })
  }
}
